// components/transactions/ResendTokenButton.tsx
import { useState } from "react";
import { useAppDispatch } from "@/redux/hook";
import { ResendTokenThunk } from "@/redux/features/actions/transactions";
import { Modal } from "antd";
import { IconExclamationCircle } from "@tabler/icons-react";
import { toast } from "react-toastify";

interface ResendTokenButtonProps {
  transaction: {
    id: string;
    product?: { productId: string };
    phone_number: string;
    user_id?: string;
    status: string;
  };
}

export const ResendTokenButton = ({ transaction }: ResendTokenButtonProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleResendToken = async () => {
    if (transaction.status !== "SUCCESS") return;
    
    setIsLoading(true);
    try {
      await dispatch(
        ResendTokenThunk({
          transactionId: transaction.id,
          productId: transaction.product?.productId || "",
          phoneNumber: transaction.phone_number,
          userId: transaction.user_id || "",
        })
      ).unwrap();
      toast.success("Token resent successfully!");
    } catch (error) {
      toast.error("Failed to resend token");
      console.error("Resend token error:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  if (transaction.status !== "SUCCESS") return null;

  return (
    <>
      <button
        onClick={() => setShowConfirmModal(true)}
        disabled={isLoading}
        className="bg-primary font-thin border-none px-6 hover:bg-primary-dark text-white text-xs py-2 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? "Sending..." : "Resend Token"}
      </button>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <IconExclamationCircle className="text-yellow-500" />
            <span>Confirm Resend</span>
          </div>
        }
        open={showConfirmModal}
        onOk={handleResendToken}
        onCancel={() => setShowConfirmModal(false)}
        okText="Yes, Resend"
        cancelText="Cancel"
        okButtonProps={{
          className: "!bg-primary !border-primary hover:!bg-primary-dark",
        }}
        confirmLoading={isLoading}
      >
        <p>Are you sure you want to resend this token?</p>
        <p className="text-gray-500 text-sm mt-2">
          This will attempt to send the token again to {transaction.phone_number}.
        </p>
      </Modal>
    </>
  );
};